import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ApiError, api } from '../api';
import { EmptyState, ErrorNotice, Notice, Spinner, formatDate } from '../components/ui';
import type { NewsletterImage, NewsletterImageList } from '../types';

/**
 * Pictures for the newsletter, and which one heads it.
 *
 * The header picture is chosen from here rather than from the settings
 * screen, because choosing it means looking at it.
 */
/** One decimal place, the way a phone reports a photo's size. */
function megabytes(bytes: number): string {
  return `${Math.round((bytes / 1048576) * 10) / 10}MB`;
}

/**
 * How a picture will actually appear as the header.
 *
 * The same arithmetic the server does when it renders one: capped at the
 * display width, never scaled up, height in proportion. "Soft" means the
 * source has fewer than two pixels for every one drawn, which is what a phone
 * screen wants — it is a note, not a problem, so nothing is refused for it.
 */
function headerSize(
  image: { width: number; height: number },
  displayWidth: number,
): { width: number; height: number; soft: boolean } {
  const width = Math.min(displayWidth, image.width);

  return {
    width,
    height: Math.round((image.height * width) / Math.max(1, image.width)),
    soft: image.width < displayWidth * 2,
  };
}

export function NewsletterImagesPage() {
  const [data, setData] = useState<NewsletterImageList | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  const [flash, setFlash] = useState<string | null>(null);
  const fileInput = useRef<HTMLInputElement>(null);

  function load() {
    api.get<NewsletterImageList>('/newsletter/images').then(setData).catch(setError);
  }

  useEffect(load, []);

  function say(message: string) {
    setFlash(message);
    window.setTimeout(() => setFlash(null), 5000);
  }

  async function upload(file: File) {
    setError(null);

    // Answered here rather than by the server, because the server cannot
    // answer until the whole picture has arrived. A 20MB photograph on rural
    // broadband is several minutes of uploading before being told no.
    const limit = data?.max_bytes ?? 0;
    if (limit > 0 && file.size > limit) {
      // An ApiError rather than a plain Error because ErrorNotice shows only
      // ApiError messages — everything else is an unexpected fault and gets
      // the generic wording. This is the same 413 the server would have
      // given, answered a few minutes earlier.
      setError(new ApiError(
        413,
        'too_large',
        `That picture is ${megabytes(file.size)}, and the limit is ${megabytes(limit)}. `
        + 'Most phones can be asked to send a smaller copy — look for "Actual size" or '
        + '"Large" rather than "Original" when sharing it.',
      ));
      if (fileInput.current) fileInput.current.value = '';
      return;
    }

    setBusy(true);
    try {
      const form = new FormData();
      form.append('image', file);
      const result = await api.postForm<{ image: NewsletterImage }>('/newsletter/images', form);
      say(
        `Added. Saved at ${result.image.width}×${result.image.height}, ${result.image.size} — `
        + 'resized and stripped of any location the camera recorded.',
      );
      load();
    } catch (failure) {
      setError(failure);
    } finally {
      setBusy(false);
      if (fileInput.current) fileInput.current.value = '';
    }
  }

  async function useAsHeader(image: NewsletterImage | null) {
    setError(null);
    try {
      const result = await api.put<NewsletterImageList>('/newsletter/logo', {
        image_id: image?.id ?? 0,
      });
      setData(result);

      if (image === null) {
        say('Header picture cleared. Newsletters will head themselves with the association’s name.');
        return;
      }

      // The useful thing to say is not "done" but what it will actually look
      // like, in the same numbers the guidance above is written in.
      const shown = headerSize(image, result.logo_display_width);
      say(
        `That picture now heads every newsletter, shown at ${shown.width}×${shown.height}.`
        + (shown.soft
          ? ` It is only ${image.width} across, so it may look a little soft on a phone —`
            + ` ${result.logo_display_width * 2} or wider stays crisp.`
          : ''),
      );
    } catch (failure) {
      setError(failure);
    }
  }

  async function remove(image: NewsletterImage) {
    if (!window.confirm(`Remove this picture? Any newsletter still using it will lose it.`)) return;
    setError(null);
    try {
      await api.del(`/newsletter/images/${image.id}`);
      load();
    } catch (failure) {
      setError(failure);
    }
  }

  return (
    <>
      <p className="muted small">
        <Link to="/email">← Back to email</Link>
      </p>

      <div className="page-head">
        <h1>Newsletter pictures</h1>
      </div>

      <p className="muted">
        Upload the association's logo and any photographs you want to use. Pictures are resized to
        fit and re-saved, which also removes the location a camera records in a photograph — worth
        knowing if you are photographing somebody's apiary.
      </p>

      <ErrorNotice error={error} />
      {flash ? <Notice kind="success">{flash}</Notice> : null}

      <div className="card">
        <label className="field">
          <span>Add a picture</span>
          <input
            ref={fileInput}
            type="file"
            accept="image/jpeg,image/png,image/gif"
            disabled={busy}
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) void upload(file);
            }}
          />
          <span className="hint">
            JPEG, PNG or GIF{data ? `, up to ${megabytes(data.max_bytes)}` : null}. Straight off a
            phone is fine — it will be made smaller.
          </span>
        </label>
        {busy ? <Spinner label="Uploading" /> : null}

        {data === null ? null : (
          <details className="logo-advice">
            <summary>What size should the header logo be?</summary>
            <ul>
              <li>
                <strong>About {data.logo_display_width * 2} pixels wide.</strong> The header is
                drawn at {data.logo_display_width} — half the width of the newsletter, so it does
                not crowd out the line underneath. Uploading double that keeps it sharp on a phone,
                where the screen packs two dots into every pixel. Bigger than that is not wrong, it
                just makes the email heavier for no visible gain.
              </li>
              <li>
                <strong>Wide rather than tall.</strong> Around{' '}
                {data.logo_display_width * 2}&nbsp;×&nbsp;{data.logo_display_width * 2 / 3} works
                well. A tall logo pushes the writing off the first screen, which is the part people
                actually read.
              </li>
              <li>
                <strong>A PNG with a transparent background.</strong> The header band is honey gold,
                so a JPEG — which cannot be transparent — arrives with a white box around it. A
                light or white logo reads best against that gold; a dark one goes muddy.
              </li>
              <li>
                Nothing here has to be exact. Anything is accepted and scaled to fit; this is only
                what looks best.
              </li>
            </ul>
          </details>
        )}
      </div>

      {data === null && error === null ? <Spinner label="Loading pictures" /> : null}

      {data !== null && data.images.length === 0 ? (
        <EmptyState title="No pictures yet">
          The newsletter will head itself with the association's name in type until you add a logo.
        </EmptyState>
      ) : null}

      {data !== null && data.images.length > 0 ? (
        <div className="image-grid">
          {data.images.map((image) => {
            const isHeader = data.logo_image_id === image.id;
            return (
              <figure key={image.id} className={`image-tile${isHeader ? ' is-header' : ''}`}>
                <img src={image.url} alt={image.original_name ?? 'Uploaded picture'} loading="lazy" />
                <figcaption>
                  {isHeader ? <span className="pill pill-active">Newsletter header</span> : null}
                  <span className="block small">
                    {image.width}×{image.height} · {image.size}
                  </span>
                  {isHeader ? (
                    <span className="block small muted">
                      Shown in the email at {headerSize(image, data.logo_display_width).width}×
                      {headerSize(image, data.logo_display_width).height}
                      {headerSize(image, data.logo_display_width).soft
                        ? ` — under ${data.logo_display_width * 2} across, so a little soft on a phone`
                        : ''}
                    </span>
                  ) : null}
                  <span className="muted block small">
                    {image.uploaded_by_name ?? 'Unknown'}, {formatDate(image.uploaded_at.slice(0, 10))}
                  </span>
                  <span className="image-actions">
                    {isHeader ? (
                      <button type="button" className="link" onClick={() => void useAsHeader(null)}>
                        Stop using as header
                      </button>
                    ) : (
                      <button type="button" className="link" onClick={() => void useAsHeader(image)}>
                        Use as header
                      </button>
                    )}
                    <button type="button" className="link" onClick={() => void remove(image)}>
                      Remove
                    </button>
                  </span>
                </figcaption>
              </figure>
            );
          })}
        </div>
      ) : null}
    </>
  );
}
